import dayjs from "dayjs";
import { useShallow } from "zustand/shallow";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Grid, IconButton } from "@mui/material";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { mdiCalendarOutline, mdiPencil } from "@mdi/js";

import AppBar from "components/AppBar";
import ContrastIcon from "components/ContrastIcon";
import PageLayout from "components/PageLayout";
import PageLoader from "components/PageLoader";
import DataIconPlaceholder from "components/DataIconPlaceholder";
import useBoundStore from "store";
import { AugmentedEvent } from "store/appDataSlice";
import useInterval from "hooks/useInterval";
import useNotifier from "hooks/useNotifier";
import getNounCount from "utils/getNounCount";
import isActiveCountdown from "utils/isActiveCountdown";
import LocalStorage from "services/LocalStorage";
import EventApi from "api/event";
import VoteApi, { VehicleType, VoteType } from "api/vote";
import { addArrayItem } from "utils/arrayOfObjectsOperations";
import { getDriverOfCurrentUser, getDrivers } from "utils/drivers";
import { hasGroupRole } from "utils/groupRoles";
import EventAppsApi from "api/event-apps";
import EventAppsMenu from "./EventAppsMenu";
import EventVoteButtons from "./EventVoteButtons";
import EventDetails from "./EventDetails";
import EventCountdown from "./EventCountdown";
import EventLocation from "./EventLocation";
import EventSpeedDial from "./EventSpeedDial";
import EventWeather from "./EventWeather";
import GoogleCalendarDialog from "./GoogleCalendarDialog";
import VehicleSelectionDialog from "./VehicleSelectionDialog";
import EventStats from "./EventStats";
import { EventCountdownType } from "./EventCountdown/EventCountdown";

type DialogType = "google-calendar" | "vehicle-selection";

export interface EventProps {
  backAction?: boolean;

  onArchiveEvent?: () => void;
  onDeleteEvent?: () => void;
  onEditEvent: () => void;
  onEventAppsMenuItemClick: (route: string) => void;
  onViewParticipants: () => void;
  onViewPending: () => void;
  onViewVehicles: () => void;
}

function Event(props: EventProps) {
  const {
    backAction = true,

    onArchiveEvent,
    onDeleteEvent,
    onEditEvent,
    onEventAppsMenuItemClick,
    onViewParticipants,
    onViewPending,
    onViewVehicles,
  } = props;
  const [isDialogOpen, setIsDialogOpen] = useState<DialogType | null>(null);
  const [countdownType, setCountdownType] = useState<EventCountdownType>("hide");
  const initialLastSeenUpdate = useRef(false);
  const { auth } = useBoundStore(useShallow((state) => ({ auth: state.user.auth })));
  const appDataInitialized = useBoundStore((state) => state.appData.initialized);
  const googleCalendar = useBoundStore((state) => state.generalSettings.googleCalendar);
  const setGeneralSettings = useBoundStore((state) => state.generalSettings.set);
  const { isAppFocused, isMobileDevice } = useBoundStore(
    useShallow((state) => ({
      isMobileDevice: state.miscellaneous.isMobileDevice,
      isAppFocused: state.miscellaneous.isAppFocused,
    }))
  );
  const selected = useBoundStore((state) => state.selected);
  const { events, isLoadingAppData, groups, groupsUsers, stateVotes, setAppData } = useBoundStore(
    useShallow((state) => ({
      events: state.appData.events,
      isLoadingAppData: state.appData.isLoading,
      groups: state.appData.groups,
      groupsUsers: state.appData.groupsUsers,
      stateVotes: state.appData.votes,
      setAppData: state.appData.set,
    }))
  );
  const { showNotification } = useNotifier();
  const localStorage = LocalStorage.getInstance();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const eventApi = useMemo(() => new EventApi(showNotification), [showNotification]);
  const voteApi = useMemo(() => new VoteApi(showNotification), [showNotification]);
  const eventAppsApi = useMemo(() => new EventAppsApi(showNotification), [showNotification]);
  const event = events.find((e) => e.id === selected.eventId);
  const group = groups.find((g) => g.id === selected.groupId);
  const votes = stateVotes.filter((vote) => vote.event_id === event?.id);
  const userVote = votes.find((vote) => vote.uid === auth!.id);
  const users = groupsUsers.filter((user) => group?.users.includes(user.uid));
  const isAdminUser =
    Boolean(group?.admins.find((id) => id === auth!.id)) || hasGroupRole(group, auth!.id, "manage-events");
  const drivers = getDrivers(votes, users);
  const driverOfCurrentUser = getDriverOfCurrentUser(drivers, auth?.id);

  /**
   * If user is still needs to vote set the timestamp when the user was last seen.
   * This can't happen sooner than 1 minutes form the last time the timestamp was
   * set.
   */
  const updateLastSeen = useCallback(async (): Promise<void> => {
    if (!event || !group) {
      return;
    }

    // If user has not voted yet, add vote
    if (!userVote) {
      const { vote, error } = await voteApi.addVote({
        uid: auth!.id,
        event_id: event.id,
        group_id: group.id,
        type: VoteType.PENDING,
      });

      // Explanation: When a user navigates directly to an event via a link or notification,
      // the real-time connection may not establish properly. This can cause the user's vote
      // to not be added to the state, resulting in an error.
      if (!error && vote) {
        setAppData({ votes: addArrayItem(vote, stateVotes) });
      }

      return;
    }

    // If user has already voted, update the timestamp when the user was last seen
    // if the last update was more than 59 seconds ago. 59 seconds is used instead of
    // 60 seconds to account for use interval delay which is 60 seconds.
    // This is to prevent unnecessary updates to the database.
    const lastUpdate = dayjs().diff(userVote.updated_at, "seconds");
    if (lastUpdate < 59) {
      return;
    }

    // Update vote timestamp
    if (event.status === "open" && userVote.type === VoteType.PENDING) {
      await voteApi.updateVote(userVote.id, {
        type: userVote.type,
      });
    }
  }, [event, group, userVote, voteApi]);

  /**
   * Update last seen timestamp every minute
   */
  useInterval(updateLastSeen, 60000);

  /**
   * Update last seen on component mount
   */
  useEffect(() => {
    if (!initialLastSeenUpdate.current && appDataInitialized) {
      updateLastSeen();
      initialLastSeenUpdate.current = true;
    }
  }, [appDataInitialized, updateLastSeen]);

  /**
   * On pressing back button close opened dialogs
   */
  useEffect(() => {
    if (navigationType === "POP") {
      setIsDialogOpen(null);
    }
  }, [navigationType]);

  useEffect(() => {
    (async () => {
      if (isAppFocused && event && group) {
        const { data } = await eventAppsApi.getActiveApps(event.id, group.id);
        selected.set({ eventApps: data?.active_apps });
      }
    })();
  }, [event, group, isAppFocused]);

  /**
   * Renders app bar
   *
   * @returns
   */
  const renderAppBar = (e?: AugmentedEvent) => {
    const actions: Array<React.ReactElement> = [];
    if (selected.eventApps) {
      actions.push(<EventAppsMenu key='apps' onMenuItemSelect={onEventAppsMenuItemClick} />);
    }

    if (isAdminUser) {
      actions.push(
        <IconButton key='action-1' onClick={onEditEvent} size='large'>
          <ContrastIcon path={mdiPencil} size={1} title='Edit event' />
        </IconButton>
      );
    }

    return (
      <AppBar
        backAction={backAction}
        onBack={() => {
          // Navigate to home if group does not exist
          if (!group && isMobileDevice) {
            navigate(-2);
          } else {
            navigate(-1);
          }
        }}
        actions={actions}
        title={e?.name ?? "GroupUp"}
      />
    );
  };

  /** Render placeholder if group or event is undefined */
  if (!event || !group) {
    if (isLoadingAppData) {
      return (
        <PageLayout appBar={renderAppBar()}>
          <PageLoader text='Synchronize event...' />
        </PageLayout>
      );
    }

    return (
      <PageLayout appBar={renderAppBar()}>
        <DataIconPlaceholder
          icon={mdiCalendarOutline}
          text='Oops...it seems like you are not part of the group or the event finished. Please contact the group owner!'
        />
      </PageLayout>
    );
  }

  const activeCountdown = isActiveCountdown(event.vote_before);

  /**
   * Handle open for google calendar and vehicles dialogs
   */
  function handleOpenDialog(type: DialogType): void {
    setIsDialogOpen(type);
    navigate(location.pathname);
  }

  /**
   * Handle close for google calendar and vehicles dialogs
   */
  function handleCloseDialog(): void {
    setIsDialogOpen(null);
    navigate(-1);
  }

  /**
   * Hide google calendar dialog so in the future it won't be show anymore.
   * It can be enabled back in settings
   */
  function handleHideGoogleCalendarDialog(): void {
    setGeneralSettings({ googleCalendar: false });

    localStorage.setGoogleCalendarEvent(false);
    handleCloseDialog();
  }

  /**
   * Handle user vote
   */
  const handleVote = async (): Promise<void> => {
    const participants = votes.filter((vote) => vote.type === VoteType.PARTICIPATING);
    if (event.participants_limit === participants.length && (!userVote || userVote?.type === VoteType.PENDING)) {
      showNotification({
        message: `This event is limited to ${getNounCount("participant", event.participants_limit)}.`,
        severity: "info",
      });

      return;
    }

    // Trigger google calendar
    if (googleCalendar && (!userVote || userVote.type === VoteType.PENDING)) {
      handleOpenDialog("google-calendar");
    }

    if (!userVote) {
      await voteApi.addVote({
        uid: auth!.id,
        event_id: event.id,
        group_id: group.id,
        type: VoteType.PARTICIPATING,
      });
      return;
    }

    const voteType = userVote.type === VoteType.PENDING ? VoteType.PARTICIPATING : VoteType.PENDING;

    // Remove user car seat if vote type is 'pending'
    if (voteType === VoteType.PENDING && driverOfCurrentUser) {
      const { error } = await voteApi.removeOwnVehicleSeat(driverOfCurrentUser.uid, event.id);
      if (error) {
        return;
      }
    }

    await voteApi.updateVote(userVote.id, { type: voteType });
  };

  /**
   * Confirm vehicle selection
   *
   * @param type
   * @param availableSeats
   */
  const handleConfirmVehicle = async (type: VehicleType, availableSeats: number): Promise<void> => {
    if (userVote) {
      // Remove user car seat if vote type is 'pending'
      if (driverOfCurrentUser) {
        const { error } = await voteApi.removeOwnVehicleSeat(driverOfCurrentUser.uid, event.id);
        if (error) {
          return;
        }
      }

      await voteApi.updateVote(userVote.id, {
        vehicle_details: {
          ...userVote?.vehicle_details,
          availableSeats,
          type,
        },
      });
    }

    handleCloseDialog();
  };

  /**
   * Handle remove selected vehicle
   */
  const handleRemoveVehicle = async (): Promise<void> => {
    if (userVote) {
      await voteApi.updateVote(userVote.id, {
        ...userVote,
        vehicle_details: null,
      });
    }
    handleCloseDialog();
  };

  /**
   * Handle stop countdown
   */
  const handleStopCountdown = async () => {
    await eventApi.updateEvent(event.id, {
      details: event.details ?? "",
      end_date: event.end_date,
      icon: event.icon,
      location: event.location,
      name: event.name,
      participants_limit: event.participants_limit,
      start_date: event.start_date,
      status: "open",
      vote_before: new Date().toISOString(),
      weather: event.weather,
    });
  };

  /**
   * Handle archive event
   */
  const handleArchiveEvent = async (): Promise<void> => {
    const { error } = await eventApi.updateEvent(event.id, {
      details: event.details ?? "",
      end_date: event.end_date,
      icon: event.icon,
      location: event.location,
      name: event.name,
      participants_limit: event.participants_limit,
      start_date: event.start_date,
      status: "closed",
      vote_before: event.vote_before,
      weather: event.weather,
    });

    if (!error && isMobileDevice) {
      navigate(-1);
    }

    // Triggered for desktop devices
    if (onArchiveEvent) {
      onArchiveEvent();
    }
  };

  /**
   * Handle delete event
   */
  const handleDeleteEvent = async (): Promise<void> => {
    const { error } = await eventApi.deleteEvent(event.id);
    if (onDeleteEvent) {
      onDeleteEvent();
    }

    if (!error && isMobileDevice) {
      navigate(-1);
    }
  };

  /**
   * Handle reset votes
   */
  const handleResetVotes = async (): Promise<void> => {
    await voteApi.resetVotes(event.id, group.id);
  };

  return (
    <PageLayout
      appBar={renderAppBar(event)}
      fab={
        <EventSpeedDial
          activeCountdown={activeCountdown}
          event={event}
          groupId={selected.groupId}
          handleArchiveEvent={handleArchiveEvent}
          handleDeleteEvent={handleDeleteEvent}
          handleResetVotes={handleResetVotes}
          handleResumeEvent={onEditEvent}
          handleStopCountdown={handleStopCountdown}
          isAdmin={isAdminUser}
        />
      }
    >
      <Box p={2}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <EventDetails event={event} />
          </Grid>

          <Grid item xs={12}>
            <EventStats
              users={users}
              event={event}
              votes={votes}
              onViewParticipants={onViewParticipants}
              onViewPending={onViewPending}
              onViewVehicles={onViewVehicles}
            />
          </Grid>

          {event.location ? (
            <Grid item xs={12}>
              <EventLocation location={event.location} />
            </Grid>
          ) : null}

          {event.weather ? (
            <Grid item xs={12}>
              <EventWeather event={event} />
            </Grid>
          ) : null}

          <Grid item xs={12}>
            <EventCountdown
              countdownType={countdownType}
              event={event}
              onTypeChange={(type) => setCountdownType(type)}
            />
          </Grid>

          <Grid item xs={12}>
            <EventVoteButtons
              countdownType={countdownType}
              onVehicleClick={() => handleOpenDialog("vehicle-selection")}
              onVote={handleVote}
              userVote={userVote}
            />
          </Grid>
        </Grid>

        <GoogleCalendarDialog
          event={event}
          onClose={handleCloseDialog}
          onHide={handleHideGoogleCalendarDialog}
          open={isDialogOpen === "google-calendar"}
        />
        <VehicleSelectionDialog
          onClose={handleCloseDialog}
          onConfirm={handleConfirmVehicle}
          onRemove={handleRemoveVehicle}
          open={isDialogOpen === "vehicle-selection"}
          vehicle={userVote?.vehicle_details}
        />
      </Box>
    </PageLayout>
  );
}

export default Event;
